import { useState } from 'react';
import { toast } from 'sonner';
import { SYNC_TYPES } from './useBroadcastSync';

/**
 * Encapsulates the six bulk operations dispatchers run from the Imports
 * dashboard (Change Status / Change LFD / Set Terminal / Change Yard /
 * Check Availability / Auto Status from LFD).
 *
 * The dashboard owns the *selection state* (which rows are checked) and
 * the *form state* (the chosen status / LFD / terminal / yard inside the
 * BulkUpdateModal); this hook owns the *side-effects* — the network calls,
 * cross-tab broadcasts, toasts, and list refresh — and the two booleans
 * the UI binds to spinners.
 *
 * Why a hook (not a plain util)? `bulkUpdating` and `bulkAvailabilityChecking`
 * each disable buttons / show spinners on multiple widgets at once. Keeping
 * the booleans in React state — owned alongside the handlers — guarantees
 * they always re-render the consumers that need them.
 */
export const useShipmentBulkActions = ({
  api,
  selectedShipments,
  selectedCount,
  deselectAll,
  broadcast,
  fetchShipments,
  masterData,
  // Modal close + value-reset callbacks supplied by the dashboard so the
  // hook never owns modal state — UI state stays with the UI.
  closeStatusModal,
  closeLFDModal,
  closeTerminalModal,
  closeYardModal,
}) => {
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkAvailabilityChecking, setBulkAvailabilityChecking] = useState(false);

  // Bulk status change — also auto-sets `mty_return` when moving rows into
  // "Container Returned" so dispatchers don't have to set the return date by
  // hand for the most common transition.
  const handleBulkStatusUpdate = async (bulkStatus) => {
    if (!bulkStatus || selectedCount === 0) return;
    setBulkUpdating(true);
    try {
      const updateData = { status: bulkStatus };
      if (bulkStatus === 'Container Returned') {
        const today = new Date().toISOString().split('T')[0];
        updateData.mty_return = `${today}T12:00:00`;
      }

      const updatePromises = Array.from(selectedShipments).map(shipmentId =>
        api.put(`/shipments/${shipmentId}`, updateData),
      );
      await Promise.all(updatePromises);

      Array.from(selectedShipments).forEach(shipmentId => {
        broadcast(SYNC_TYPES.STATUS_UPDATED, { shipmentId, status: bulkStatus });
      });

      toast.success(`Updated ${selectedCount} shipments to "${bulkStatus}"`);
      deselectAll();
      closeStatusModal?.();
      fetchShipments();
    } catch (error) {
      toast.error('Failed to update some shipments');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Bulk LFD update — bulkLFD is a Date object from the date picker.
  const handleBulkLFDUpdate = async (bulkLFD) => {
    if (!bulkLFD || selectedCount === 0) return;
    setBulkUpdating(true);
    try {
      const lfdValue = `${bulkLFD.toISOString().split('T')[0]}T12:00:00`;
      const updatePromises = Array.from(selectedShipments).map(shipmentId =>
        api.put(`/shipments/${shipmentId}`, { last_free_date: lfdValue }),
      );
      await Promise.all(updatePromises);
      toast.success(`Updated LFD for ${selectedCount} shipments`);
      deselectAll();
      closeLFDModal?.();
      fetchShipments();
    } catch (error) {
      toast.error('Failed to update some shipments');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Bulk Terminal assign — uses the dedicated server endpoint which atomically
  // clears the "auto_assigned" flag (operator chose explicitly = source of truth).
  const handleBulkTerminalUpdate = async (bulkTerminal) => {
    if (!bulkTerminal || selectedCount === 0) return;
    setBulkUpdating(true);
    try {
      const ids = Array.from(selectedShipments);
      const res = await api.post('/shipments/bulk-set-terminal', {
        shipment_ids: ids,
        terminal_id: bulkTerminal,
      });
      const name = res?.data?.terminal_name
        || masterData.terminals?.find(t => t.id === bulkTerminal)?.name
        || 'selected terminal';
      toast.success(`Assigned "${name}" to ${res?.data?.updated_count ?? ids.length} shipments`);
      deselectAll();
      closeTerminalModal?.();
      fetchShipments();
    } catch {
      toast.error('Failed to assign terminal');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Bulk Yard update — straight per-row PUT (no dedicated endpoint needed).
  const handleBulkYardUpdate = async (bulkYard) => {
    if (!bulkYard || selectedCount === 0) return;
    setBulkUpdating(true);
    try {
      const updatePromises = Array.from(selectedShipments).map(shipmentId =>
        api.put(`/shipments/${shipmentId}`, { yard_id: bulkYard }),
      );
      await Promise.all(updatePromises);

      const yardName = masterData.yards?.find(y => y.id === bulkYard)?.name || 'selected yard';
      toast.success(`Updated Yard to "${yardName}" for ${selectedCount} shipments`);
      deselectAll();
      closeYardModal?.();
      fetchShipments();
    } catch (error) {
      toast.error('Failed to update some shipments');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Bulk Availability Check — fan-out to terminal scrapers. Capped at 20
  // server-side too, but we short-circuit on the client to give the user a
  // clearer error than the API's generic 400.
  const handleBulkAvailabilityCheck = async () => {
    const count = selectedShipments.size;
    if (count === 0) return;
    if (count > 20) {
      toast.error('Maximum 20 shipments per bulk check');
      return;
    }
    setBulkAvailabilityChecking(true);
    try {
      const response = await api.post(
        '/terminal-integration/bulk-check',
        Array.from(selectedShipments),
      );
      const { summary, results } = response.data;
      const queuedCount = results?.filter(r => r.status === 'queued').length || 0;

      if (summary.success > 0) toast.success(`Checked ${summary.success} containers successfully`);
      if (queuedCount > 0) toast.success(`${queuedCount} containers queued for background check`);
      if (summary.errors > 0) toast.warning(`${summary.errors} containers had errors`);
      if (summary.pending > 0) {
        toast.info(`${summary.pending} containers need credentials - check Settings > Terminal Integration`);
      }

      fetchShipments();
      deselectAll();
    } catch (error) {
      // Sanitize backend detail strings — they can include Python tracebacks
      // or DB errors. Map known cases to friendly copy; otherwise show generic.
      const status = error.response?.status;
      let msg = 'Could not check availability — try again in a moment.';
      if (status === 401 || status === 403) {
        msg = 'You don\'t have permission to run availability checks.';
      } else if (status === 429) {
        msg = 'Too many checks recently — please wait a minute before retrying.';
      } else if (status === 400) {
        msg = error.response?.data?.detail || 'Some shipments are missing required data for tracking.';
      }
      toast.error(msg);
    } finally {
      setBulkAvailabilityChecking(false);
    }
  };

  // Bulk Auto-Status — flips selected rows to Available / Trouble based on
  // each shipment's existing LFD, without re-scraping. Cheap, no rate-limit.
  const handleBulkAutoStatus = async () => {
    const count = selectedShipments.size;
    if (count === 0) return;
    setBulkUpdating(true);
    try {
      const response = await api.post(
        '/terminal-integration/bulk-auto-status',
        Array.from(selectedShipments),
      );
      const { summary } = response.data;
      if (summary.updated > 0) {
        toast.success(`Updated ${summary.updated} containers to Available/Trouble based on LFD`);
      }
      if (summary.skipped > 0) {
        toast.info(`${summary.skipped} containers skipped (no LFD or already updated)`);
      }
      fetchShipments();
      deselectAll();
    } catch (error) {
      const status = error.response?.status;
      let msg = 'Could not auto-update statuses — try again in a moment.';
      if (status === 401 || status === 403) {
        msg = 'You don\'t have permission to update statuses.';
      } else if (status === 400) {
        msg = error.response?.data?.detail || 'Selected shipments are missing required data.';
      }
      toast.error(msg);
    } finally {
      setBulkUpdating(false);
    }
  };

  return {
    bulkUpdating,
    bulkAvailabilityChecking,
    handleBulkStatusUpdate,
    handleBulkLFDUpdate,
    handleBulkTerminalUpdate,
    handleBulkYardUpdate,
    handleBulkAvailabilityCheck,
    handleBulkAutoStatus,
  };
};
